/**
 * TreatX Coordinator Data Context
 * =================================
 * Phase 2B: Removed ALL mock data, sessionStorage, and frontend-only logic.
 * All patient data now flows through backend APIs:
 *   GET /api/coordinator/patients
 *   POST /api/coordinator/patients
 *   PUT /api/coordinator/patients/:id
 *   POST /api/coordinator/patients/:id/notes
 *   DELETE /api/coordinator/patients/:id/notes/:noteId
 *   GET /api/coordinator/stats
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
const _COORD_ROLES = ['coordinator', 'clinic_coordinator', 'clinic_admin', 'provider_admin'];
import axios from 'axios';

const CoordinatorDataContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useCoordinatorData = () => {
  const context = useContext(CoordinatorDataContext);
  if (!context) {
    throw new Error('useCoordinatorData must be used within CoordinatorDataProvider');
  }
  return context;
};

// Patient journey stages — UI constants only (no business logic)
export const JOURNEY_STAGES = {
  INTAKE: { id: 'INTAKE', label: 'Intake Submitted', color: 'bg-blue-100 text-blue-700' },
  CLINIC_SELECTION: { id: 'CLINIC_SELECTION', label: 'Selecting Clinics', color: 'bg-purple-100 text-purple-700' },
  OFFERS_PENDING: { id: 'OFFERS_PENDING', label: 'Awaiting Offers', color: 'bg-amber-100 text-amber-700' },
  OFFERS_RECEIVED: { id: 'OFFERS_RECEIVED', label: 'Reviewing Offers', color: 'bg-orange-100 text-orange-700' },
  PAYMENT_PENDING: { id: 'PAYMENT_PENDING', label: 'Payment Pending', color: 'bg-yellow-100 text-yellow-700' },
  DEPOSIT_PAID: { id: 'DEPOSIT_PAID', label: 'Deposit Paid', color: 'bg-green-100 text-green-700' },
  BOOKING_CONFIRMED: { id: 'BOOKING_CONFIRMED', label: 'Booking Confirmed', color: 'bg-teal-100 text-teal-700' },
  TRAVEL_SCHEDULED: { id: 'TRAVEL_SCHEDULED', label: 'Travel Scheduled', color: 'bg-sky-100 text-sky-700' },
  TREATMENT_COMPLETE: { id: 'TREATMENT_COMPLETE', label: 'Treatment Complete', color: 'bg-emerald-100 text-emerald-700' },
  CLOSED: { id: 'CLOSED', label: 'Case Closed', color: 'bg-slate-100 text-slate-700' },
};

// Normalize snake_case API response to camelCase for frontend components
const normalizePatient = (p) => ({
  id: p.patient_id,
  patientId: p.patient_id,
  name: p.name,
  email: p.email,
  phone: p.phone,
  country: p.country,
  city: p.city,
  treatment: p.treatment,
  treatmentCategory: p.treatment_category,
  budget: p.budget,
  preferredDate: p.preferred_date,
  journeyStage: p.journey_stage,
  assignedCoordinator: p.assigned_coordinator,
  isEscalated: p.is_escalated,
  healthProfile: p.health_profile || {},
  selectedClinics: p.selected_clinics || [],
  offers: p.offers || [],
  acceptedOffer: p.accepted_offer,
  notes: (p.notes || []).map(n => ({
    id: n.note_id,
    noteId: n.note_id,
    text: n.text,
    createdBy: n.created_by,
    createdAt: n.created_at,
  })),
  gdprSigned: p.gdpr_signed,
  paymentPolicySigned: p.payment_policy_signed,
  intakeDate: p.intake_date,
  lastActivity: p.last_activity,
  createdAt: p.created_at,
});

export const CoordinatorDataProvider = ({ children }) => {
  const [patientLeads, setPatientLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all patients from backend
  const fetchPatients = useCallback(async (coordinatorId = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (coordinatorId) params.coordinator_id = coordinatorId;
      const { data } = await axios.get(`${API_URL}/api/coordinator/patients`, { params });
      const normalized = (data.patients || []).map(normalizePatient);
      setPatientLeads(normalized);
      return normalized;
    } catch (err) {
      console.error('[CoordinatorData] fetchPatients error:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // TREATX-BATCH-BOOT-01 — coordinator patients: coordinator-scope only.
  const { authorized: _coord_authorized } = useAuthGate({ roles: _COORD_ROLES });
  useEffect(() => {
    if (!_coord_authorized) return;
    fetchPatients();
  }, [_coord_authorized, fetchPatients]);

  const getAllPatients = () => patientLeads;

  const getPatientsForCoordinator = (coordinatorId) =>
    patientLeads.filter((p) => p.assignedCoordinator === coordinatorId);

  const getPatientById = (patientId) =>
    patientLeads.find((p) => p.id === patientId || p.patientId === patientId);

  const getEscalatedCases = () => patientLeads.filter((p) => p.isEscalated);

  const getPatientsByStage = (stage) =>
    patientLeads.filter((p) => p.journeyStage === stage);

  // Update patient journey stage via API
  const updateJourneyStage = async (patientId, newStage) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/coordinator/patients/${patientId}`, {
        journey_stage: newStage,
      });
      if (data.success) {
        const updated = normalizePatient(data.patient);
        setPatientLeads((prev) => prev.map((p) => (p.id === patientId ? updated : p)));
      }
    } catch (err) {
      console.error('[CoordinatorData] updateJourneyStage error:', err);
    }
  };

  // Toggle escalation via API
  const toggleEscalation = async (patientId) => {
    const existing = patientLeads.find((p) => p.id === patientId);
    if (!existing) return;
    try {
      const { data } = await axios.put(`${API_URL}/api/coordinator/patients/${patientId}`, {
        is_escalated: !existing.isEscalated,
      });
      if (data.success) {
        const updated = normalizePatient(data.patient);
        setPatientLeads((prev) => prev.map((p) => (p.id === patientId ? updated : p)));
      }
    } catch (err) {
      console.error('[CoordinatorData] toggleEscalation error:', err);
    }
  };

  // Add note via API
  const addNote = async (patientId, noteText, createdBy) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/coordinator/patients/${patientId}/notes`, {
        text: noteText,
        created_by: createdBy,
      });
      if (data.success) {
        // Refetch patient to get updated notes array
        await fetchPatients();
        return {
          id: data.note.note_id,
          text: data.note.text,
          createdBy: data.note.created_by,
          createdAt: data.note.created_at,
        };
      }
    } catch (err) {
      console.error('[CoordinatorData] addNote error:', err);
    }
    return null;
  };

  // Delete note via API
  const deleteNote = async (patientId, noteId) => {
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/coordinator/patients/${patientId}/notes/${noteId}`
      );
      if (data.success) {
        await fetchPatients();
      }
    } catch (err) {
      console.error('[CoordinatorData] deleteNote error:', err);
    }
  };

  // Assign coordinator via API
  const assignCoordinator = async (patientId, coordinatorId) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/coordinator/patients/${patientId}`, {
        assigned_coordinator: coordinatorId,
      });
      if (data.success) {
        const updated = normalizePatient(data.patient);
        setPatientLeads((prev) => prev.map((p) => (p.id === patientId ? updated : p)));
      }
    } catch (err) {
      console.error('[CoordinatorData] assignCoordinator error:', err);
    }
  };

  // Get stats from current loaded data (consistent with old interface)
  const getStats = (coordinatorId = null) => {
    const patients = coordinatorId
      ? getPatientsForCoordinator(coordinatorId)
      : patientLeads;
    return {
      total: patients.length,
      intake: patients.filter((p) => p.journeyStage === 'INTAKE').length,
      clinicSelection: patients.filter((p) => p.journeyStage === 'CLINIC_SELECTION').length,
      offersPending: patients.filter((p) => p.journeyStage === 'OFFERS_PENDING').length,
      offersReceived: patients.filter((p) => p.journeyStage === 'OFFERS_RECEIVED').length,
      paymentPending: patients.filter((p) => p.journeyStage === 'PAYMENT_PENDING').length,
      depositPaid: patients.filter((p) => p.journeyStage === 'DEPOSIT_PAID').length,
      bookingConfirmed: patients.filter((p) => p.journeyStage === 'BOOKING_CONFIRMED').length,
      escalated: patients.filter((p) => p.isEscalated).length,
    };
  };

  // Update document status via API
  const updateDocumentStatus = async (patientId, documentType, signed) => {
    const field = documentType === 'gdpr' ? 'gdpr_signed' : 'payment_policy_signed';
    try {
      const { data } = await axios.put(`${API_URL}/api/coordinator/patients/${patientId}`, {
        [field]: signed,
      });
      if (data.success) {
        const updated = normalizePatient(data.patient);
        setPatientLeads((prev) => prev.map((p) => (p.id === patientId ? updated : p)));
      }
    } catch (err) {
      console.error('[CoordinatorData] updateDocumentStatus error:', err);
    }
  };

  const value = {
    patientLeads,
    loading,
    error,
    fetchPatients,
    getAllPatients,
    getPatientsForCoordinator,
    getPatientById,
    getEscalatedCases,
    getPatientsByStage,
    updateJourneyStage,
    toggleEscalation,
    addNote,
    deleteNote,
    assignCoordinator,
    getStats,
    updateDocumentStatus,
    JOURNEY_STAGES,
  };

  return (
    <CoordinatorDataContext.Provider value={value}>
      {children}
    </CoordinatorDataContext.Provider>
  );
};

export default CoordinatorDataContext;
